/**
 * Pure helpers for proxy credentials: the opaque username scheme, session-mode
 * suffixes, and connection-string builders used by the endpoint-builder UI.
 *
 * Dependency-free and client-safe (used for display) — see credentials.test.ts.
 * Username *generation* needs randomness and the control plane, so it lives in
 * the server module (`credentials-server.ts`), not here.
 *
 * A proxy credential belongs to an organization (never to a human member) and
 * draws from the org's single shared GB pool. The stored `username` is an
 * opaque token; at connect time the customer appends optional suffixes — a
 * location (`_loc_<CODE>`), a carrier (`_carrier_<NAME>`) and/or `_sticky`
 * (see STICKY_SESSION_SECONDS for the pin duration) — which the backend strips
 * for auth but keeps for upstream routing. Rotating (the default) adds no suffix.
 */

/**
 * The PRODUCTION username prefix, for client-safe DISPLAY use (marketing
 * examples, docs). The server generates and sweeps usernames under the
 * env-driven `PROXY_USERNAME_PREFIX` instead (default 'mlabs'; a staging
 * deployment sharing the prod control plane sets its own disjoint one, e.g.
 * 'staging') — never import this constant for generation or matching.
 */
export const USERNAME_PREFIX = 'mlabs'

/**
 * Max proxy credentials one org can hold. Managing credentials is open to any
 * member (see credentials/server.ts), and each create provisions a backend user
 * + draws on the shared pool, so an unbounded loop is an abuse/DoS vector — this
 * caps it. Generous for real use; raise if a customer legitimately needs more.
 */
export const MAX_CREDENTIALS_PER_ORG = 50

/** Upper bound for an admin-set per-org override. */
export const MAX_CREDENTIALS_OVERRIDE = 10_000

/** An org's effective credential cap: its admin override, else the default. */
export function credentialLimit(override: number | null | undefined): number {
  return override ?? MAX_CREDENTIALS_PER_ORG
}

/** Phrase the create form turns into a link to the support page. */
export const CONTACT_SUPPORT_PHRASE = 'contact support'

export function credentialLimitMessage(
  limit: number,
  supportUrl?: string,
): string {
  const contact = supportUrl
    ? `${CONTACT_SUPPORT_PHRASE} at ${supportUrl}`
    : CONTACT_SUPPORT_PHRASE
  return `This organization has reached its limit of ${limit} proxy credentials. Delete one before creating another, or ${contact} to request a higher limit.`
}

/**
 * How long a plain `_sticky` login pins one exit IP, in seconds: the default
 * window. The window is enforced by the proxy backend; this constant is the
 * single source of truth for the value we QUOTE to users, so every mention
 * stays in sync if the window changes (e.g. 180 → 300). Keep it aligned with
 * the backend's UPSTREAM_STICKY_DURATION (set in vh_meteredproxy's prod and
 * staging config env files).
 */
export const STICKY_SESSION_SECONDS = 300

/**
 * Bounds for a custom window, `_sticky_<N>s` or `_sticky_<N>m`. The proxies
 * accept any whole number in either unit and clamp it to this range, so the
 * builder never offers anything outside it.
 */
export const STICKY_WINDOW_MIN_SECONDS = 10
export const STICKY_WINDOW_MAX_SECONDS = 1800
export const STICKY_WINDOW_DEFAULT_SECONDS = STICKY_SESSION_SECONDS

/** One-click windows next to the builder's window input. */
export const STICKY_WINDOW_QUICK_PICKS: readonly number[] = [
  10, 30, 60, 300, 600, 1800,
]

/** The unit a window is typed in: seconds or minutes. */
export type StickyWindowUnit = 's' | 'm'

const UNIT_SECONDS: Record<StickyWindowUnit, number> = { s: 1, m: 60 }

/**
 * Any input as a valid window in seconds: converted from `unit`, rounded to
 * whole seconds and clamped to MIN..MAX, the same way the proxies treat it.
 * Anything that is not a finite number (an empty input, a stale or legacy
 * value) gives the default.
 */
export function clampStickySeconds(
  value: unknown,
  unit: StickyWindowUnit = 's',
): number {
  const n =
    typeof value === 'string' && value.trim() !== '' ? Number(value) : value
  if (typeof n !== 'number' || !Number.isFinite(n)) {
    return STICKY_WINDOW_DEFAULT_SECONDS
  }
  return Math.min(
    STICKY_WINDOW_MAX_SECONDS,
    Math.max(STICKY_WINDOW_MIN_SECONDS, Math.round(n * UNIT_SECONDS[unit])),
  )
}

/** True when a window is a whole number of minutes (1 minute or more). */
function isWholeMinutes(seconds: number): boolean {
  return seconds >= 60 && seconds % 60 === 0
}

/**
 * A window as the builder's input shows it: whole minutes in `m`, anything
 * else in `s`. 300 → { value: '5', unit: 'm' }, 90 → { value: '90', unit: 's' }.
 */
export function stickyWindowInput(seconds: number): {
  value: string
  unit: StickyWindowUnit
} {
  return isWholeMinutes(seconds)
    ? { value: String(seconds / 60), unit: 'm' }
    : { value: String(seconds), unit: 's' }
}

/** Prose for one window: "5 minutes", "1 minute", "30 seconds", "90 seconds". */
export function formatStickyWindow(seconds: number): string {
  if (isWholeMinutes(seconds)) {
    const m = seconds / 60
    return `${m} minute${m === 1 ? '' : 's'}`
  }
  return `${seconds} second${seconds === 1 ? '' : 's'}`
}

/** Compact label for a quick pick: "10s", "30s", "1m", "5m", "30m". */
export function formatStickyWindowShort(seconds: number): string {
  return isWholeMinutes(seconds) ? `${seconds / 60}m` : `${seconds}s`
}

/**
 * The token that follows `_sticky` for a custom window: `_<N>m` for a whole
 * number of minutes (`_6m`), `_<N>s` otherwise (`_90s`). Empty for the
 * default (or no) window, so a plain `_sticky` keeps meaning "the default
 * window". Out-of-range values are clamped first, so the token always names
 * the window the proxy will actually use.
 */
export function stickyWindowSuffix(seconds?: number): string {
  if (seconds === undefined) return ''
  const s = clampStickySeconds(seconds)
  if (s === STICKY_WINDOW_DEFAULT_SECONDS) return ''
  return isWholeMinutes(s) ? `_${s / 60}m` : `_${s}s`
}

/** Short form for chips/toggles, e.g. "~60s" or "~3m". */
export function stickyWindowShort(): string {
  const s = STICKY_SESSION_SECONDS
  return s >= 120 && s % 60 === 0 ? `~${s / 60}m` : `~${s}s`
}

/** Prose form for sentences, e.g. "about 60 seconds" or "about 3 minutes". */
export function stickyWindowLong(): string {
  const s = STICKY_SESSION_SECONDS
  if (s >= 120 && s % 60 === 0) {
    const m = s / 60
    return `about ${m} minute${m === 1 ? '' : 's'}`
  }
  return `about ${s} seconds`
}

/**
 * Session mode, expressed as the suffix appended to the proxy login:
 *  - `rotating` (no suffix): a fresh upstream IP per request. Rotating is the
 *    backend's default selection, so it needs no suffix at all.
 *  - `sticky`   (`_sticky`, `_sticky_<N>s` or `_sticky_<N>m`): pin the same
 *    upstream IP for STICKY_SESSION_SECONDS, or for the given window (see
 *    stickyWindowSuffix).
 */
export type SessionMode = 'rotating' | 'sticky'

export function modeSuffix(mode: SessionMode, stickySeconds?: number): string {
  return mode === 'sticky' ? `_sticky${stickyWindowSuffix(stickySeconds)}` : ''
}

/**
 * Suffix restricting selection to a single location, e.g. `_loc_ORD`. An empty
 * / undefined code means "any location" and adds nothing.
 */
export function locationSuffix(location?: string): string {
  return location ? `_loc_${location}` : ''
}

/**
 * Carriers a login can be restricted to with `_carrier_<value>`. There is no
 * fallback: if no exit in the location is on that carrier, the connection
 * fails.
 */
export const CARRIERS = [
  { value: 'att', label: 'AT&T' },
  { value: 'verizon', label: 'Verizon' },
] as const

export type Carrier = (typeof CARRIERS)[number]['value']

/** Suffix restricting selection to one carrier. Empty / undefined = any. */
export function carrierSuffix(carrier?: Carrier | ''): string {
  return carrier ? `_carrier_${carrier}` : ''
}

export interface LoginOptions {
  mode?: SessionMode
  /** Location code, e.g. `ORD`. Empty / undefined = any location. */
  location?: string
  carrier?: Carrier | ''
  /** Sticky window in seconds. Ignored unless `mode` is `sticky`. */
  stickySeconds?: number
}

/**
 * The login a customer actually sends: the stored username plus optional
 * suffixes. The proxies accept the tokens in any order; we always write them
 * in one order so the same choices give the same string:
 * `<base>[_loc_<CODE>][_carrier_att|_carrier_verizon][_sticky|_sticky_<N>s|_sticky_<N>m]`.
 *
 * Examples: `mlabs_ab12cd34`, `mlabs_ab12cd34_sticky`,
 * `mlabs_ab12cd34_sticky_30s`, `mlabs_ab12cd34_sticky_6m`,
 * `mlabs_ab12cd34_loc_ORD`, `mlabs_ab12cd34_loc_LAX_carrier_att_sticky_45s`.
 */
export function proxyLogin(
  username: string,
  { mode = 'rotating', location, carrier, stickySeconds }: LoginOptions = {},
): string {
  return `${username}${locationSuffix(location)}${carrierSuffix(carrier)}${modeSuffix(mode, stickySeconds)}`
}

/** Placeholder shown where the (never-stored) password goes in the builder. */
export const PASSWORD_PLACEHOLDER = '<PASSWORD>'

/** The public connection endpoint (NOT the control-plane API). Not secret. */
export interface ProxyConnectionInfo {
  host: string
  httpPort: number
  httpsPort: number
  socksPort: number
  http3Port: number
}

// SOCKS is exposed as `socks5h` (not `socks5`) so the client resolves DNS
// THROUGH the proxy rather than locally — the exit IP does the lookup, which is
// what customers want (no DNS leak, and geo-consistent with the exit). `https`
// is the same HTTP proxy reached over a TLS connection (client<->proxy leg
// encrypted), served on the standard 443 so it sails through TLS-only egress.
export type ProxyProtocol = 'http' | 'https' | 'socks5h'

function portFor(protocol: ProxyProtocol, info: ProxyConnectionInfo): number {
  if (protocol === 'socks5h') return info.socksPort
  if (protocol === 'https') return info.httpsPort
  return info.httpPort
}

// The scheme's implied default port, dropped from the URL when it matches so the
// https endpoint reads as `https://host` (443 is implicit). http maps to 80,
// which our gateway never uses (8080), so it always stays explicit.
const DEFAULT_PORT: Partial<Record<ProxyProtocol, number>> = {
  http: 80,
  https: 443,
}

/** `host` or `host:port`, omitting the port when it's the scheme default. */
function authority(protocol: ProxyProtocol, info: ProxyConnectionInfo): string {
  const port = portFor(protocol, info)
  return DEFAULT_PORT[protocol] === port ? info.host : `${info.host}:${port}`
}

/** Standard URL form: `http://login:password@host:port` (`:port` omitted when default). */
export function proxyUrl(
  protocol: ProxyProtocol,
  info: ProxyConnectionInfo,
  login: string,
  password: string,
): string {
  return `${protocol}://${login}:${password}@${authority(protocol, info)}`
}

/** Scraper-style `host:port:login:password` form. */
export function hostPortLogin(
  protocol: ProxyProtocol,
  info: ProxyConnectionInfo,
  login: string,
  password: string,
): string {
  return `${info.host}:${portFor(protocol, info)}:${login}:${password}`
}

/**
 * A ready-to-run curl that prints the exit IP + geo seen through the proxy,
 * using the selected protocol (so SOCKS shows `socks5h://`). ipinfo.io returns
 * JSON including city/region, which doubles as a check that a chosen location
 * is actually taking effect.
 */
export function curlSnippet(
  protocol: ProxyProtocol,
  info: ProxyConnectionInfo,
  login: string,
  password: string,
): string {
  return `curl -x "${proxyUrl(protocol, info, login, password)}" https://ipinfo.io/json`
}

function http3Authority(info: ProxyConnectionInfo): string {
  return info.http3Port === 443 ? info.host : `${info.host}:${info.http3Port}`
}

/** The HTTP/3 (MASQUE) endpoint as a URL, port omitted when it is 443. */
export function http3Url(info: ProxyConnectionInfo): string {
  return `https://${http3Authority(info)}`
}

/** RFC 9298 URI template for CONNECT-UDP requests. */
export function masqueTemplate(info: ProxyConnectionInfo): string {
  return `${http3Url(info)}/.well-known/masque/udp/{target_host}/{target_port}/`
}
